import type { ReactElement } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { LoadingIndicator } from "../ui";

export interface UseSetupGuardOptions {
  needsSetup: boolean;
  checkingSetup: boolean;
  hasNetworkError: boolean;
  /** Whether the setup wizard applies at all (e.g. brand/mode gating). When false, navigating to `setupPath` redirects to `dashboardPath`. */
  setupEnabled: boolean;
  setupPath: string;
  dashboardPath: string;
  networkErrorPath?: string;
}

/**
 * Setup-wizard redirect guard, meant to be plugged into `ProtectedRoute`'s
 * `guardElement` prop. Call this directly inside a route-level component (not
 * conditionally) so it re-runs whenever the underlying setup context changes.
 */
export function useSetupGuard(
  options: UseSetupGuardOptions,
): ReactElement | null {
  const {
    needsSetup,
    checkingSetup,
    hasNetworkError,
    setupEnabled,
    setupPath,
    dashboardPath,
    networkErrorPath = "/network-error",
  } = options;
  const location = useLocation();

  if (checkingSetup) {
    return <LoadingIndicator fullScreen />;
  }
  if (hasNetworkError && location.pathname !== networkErrorPath) {
    return <Navigate to={networkErrorPath} replace />;
  }
  if (!setupEnabled && location.pathname === setupPath) {
    return <Navigate to={dashboardPath} replace />;
  }
  if (needsSetup && location.pathname !== setupPath) {
    return <Navigate to={setupPath} replace />;
  }
  return null;
}
